import React from 'react';
import styled, { css } from 'styled-components';
import { BREAKPOINT } from '@constants';
import { CheckboxDesign } from '@contracts';
import Colors from '../Colors';

interface CustomCheckboxProps {
  checked: boolean;
  design?: CheckboxDesign;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
}

const CustomCheckbox: React.FC<CustomCheckboxProps> = ({ checked, design, onChange, disabled = false }) => {
  return (
    <CheckboxContainer
      $checked={checked}
      $design={design}
      $disabled={disabled}
      role="checkbox"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onChange?.(!checked)}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onChange?.(!checked);
        }
      }}
    >
      {checked && (
        <svg fill="none" height="9" viewBox="0 0 11 9" width="11" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true" focusable="false">
          <path
            clipRule="evenodd"
            d="m3.58975 7.77568-.00001.00001-3.225608-3.22561c-.3905245-.39052-.3905255-1.02369-.000001-1.41421.390525-.39053 1.023689-.39053 1.414209 0l2.12132 2.12132 4.72166-4.721658c.39052-.390525 1.02369-.390524 1.41418 0 .3906.390524.3906 1.023688 0 1.414218l-6.13583 6.13586z"
            fill="#001d3b"
            fillRule="evenodd"
          />
        </svg>
      )}
    </CheckboxContainer>
  );
};

const CheckboxContainer = styled.div<{
  $checked: boolean;
  $design?: CheckboxDesign;
  $disabled?: boolean;
}>`
  width: 25px;
  min-width: 25px;
  height: 25px;
  border: 2px solid ${Colors.navyBlue};
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};

  &:focus-visible {
    outline: 2px solid ${Colors.navyBlue};
    outline-offset: -2px;
    ${({ $design }) =>
      css`
        ${$design === CheckboxDesign.PinkHighlight
          ? css`
              outline-color: ${Colors.lilac};
            `
          : css``}
      `}
  }

  @media (max-width: ${BREAKPOINT.laptop}px) {
    width: 16px;
    height: 16px;
    min-width: 16px;
  }

  svg {
    width: 11px;
    height: 11px;

    @media (max-width: ${BREAKPOINT.laptop}px) {
      width: 9px;
      height: 6px;
    }
  }

  ${({ $checked, $design }) =>
    $checked &&
    css`
      ${$design === CheckboxDesign.PinkHighlight
        ? css`
            border-color: ${Colors.lilac};
            svg path {
              fill: ${Colors.lilac};
            }
          `
        : css`
            svg path {
              stroke: ${Colors.navyBlue};
            }
          `}
    `}
`;

export default CustomCheckbox;
